/** Ticking rows, so several can be acted on at once.
 *
 *  Shared by the table and the card list rather than written in each: they are two
 *  renderings of one list (`TaskList` picks by width), and a second copy of "is this
 *  one ticked" would let a phone and a laptop disagree about what is selected.
 *
 *  Deliberately shaped as one `Selection` object passed down rather than three loose
 *  props. `TaskTable` and `TaskCards` both forward it untouched, and a screen with no
 *  bulk action passes nothing - so no column is rendered and nothing about those
 *  lists changes shape just because this exists.
 *
 *  Every task is tickable, including finished ones. A greyed row with no explanation
 *  invites "why can't I tick this one", and the server answers the question properly:
 *  it reports each id it would not act on, with the reason.
 */
import { useCallback, useMemo, useState } from 'react'
import type { Task } from '@/api/client'

export interface Selection {
  has: (id: string) => boolean
  toggle: (id: string) => void
  /** Tick all of these, or untick them if every one is already ticked. */
  toggleMany: (ids: string[]) => void
  ids: string[]
  count: number
  clear: () => void
}

/** What the header box does to a selection.
 *
 *  "Untick all" only when every one is already on. With some ticked it COMPLETES the
 *  set rather than clearing it, which is what somebody who has ticked three of
 *  twenty and then reaches for the header box means by it. Clearing there would
 *  throw away work they had just done.
 *
 *  Only the ids passed in are touched, so ticking everything on a filtered list
 *  leaves a selection made on some other filter alone rather than silently widening
 *  to rows nobody can see. An empty list is a no-op for the same reason - there is
 *  nothing to iterate - which is why `all` needs no guard against `every` returning
 *  true for it. `TickAll` does need that guard, because there it decides whether the
 *  box renders ticked.
 *
 *  Pure and exported so the rule can be tested without a renderer.
 */
export function toggleAll(picked: Set<string>, ids: string[]): Set<string> {
  const all = ids.every((id) => picked.has(id))
  const next = new Set(picked)
  ids.forEach((id) => (all ? next.delete(id) : next.add(id)))
  return next
}


export function useSelection(): Selection {
  const [picked, setPicked] = useState<Set<string>>(new Set())

  const toggle = useCallback((id: string) => setPicked((s) => {
    const next = new Set(s)
    if (next.has(id)) next.delete(id)
    else next.add(id)
    return next
  }), [])

  const toggleMany = useCallback(
    (ids: string[]) => setPicked((s) => toggleAll(s, ids)), [])

  const clear = useCallback(() => setPicked(new Set()), [])

  return useMemo(() => ({
    has: (id: string) => picked.has(id),
    toggle,
    toggleMany,
    ids: [...picked],
    count: picked.size,
    clear,
  }), [picked, toggle, toggleMany, clear])
}

export function Tick({ selection, task }: { selection: Selection; task: Task }) {
  if (!task.task_id) return null
  return (
    <input type="checkbox" checked={selection.has(task.task_id)}
           onChange={() => selection.toggle(task.task_id)}
           aria-label={`Select #${task.ref}`} />
  )
}

/** The header box: all, none, or the third state that says "some".
 *
 *  `indeterminate` is not an attribute React sets, so it goes on through a ref. A
 *  header box that reads unticked while three rows are selected is actively
 *  misleading about what a bulk action is about to do.
 */
export function TickAll({ selection, tasks }: { selection: Selection; tasks: Task[] }) {
  const ids = tasks.map((t) => t.task_id).filter(Boolean)
  const on = ids.length > 0 && ids.every((id) => selection.has(id))
  const some = !on && ids.some((id) => selection.has(id))
  return (
    <input type="checkbox" checked={on} disabled={ids.length === 0}
           ref={(el) => { if (el) el.indeterminate = some }}
           onChange={() => selection.toggleMany(ids)}
           aria-label={on ? 'Clear selection' : 'Select all shown'} />
  )
}
